import {
	useAssignProductsToOrganizationMutation,
	useGetAllOrganizationQuery,
} from '@codezo/graphql';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { BrandSelector } from '../../components/selectors/BrandSelector';
import { InitialsAvatar } from '../../components/ui/initials-avatar';
import { Label } from '../../components/ui/label';
import { cn } from '../../lib/utils';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

interface AssignToClientModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	productIds: string[];
	onSuccess: () => void;
}

export function AssignToClientModal({
	open,
	onOpenChange,
	productIds,
	onSuccess,
}: AssignToClientModalProps) {
	const [selectedOrg, setSelectedOrg] = useState('');
	/* Optional, and only once a client is picked — a brand belongs to one client. */
	const [selectedBrand, setSelectedBrand] = useState('');
	const [query, setQuery] = useState('');
	const debouncedQuery = useDebouncedValue(query);
	const [page, setPage] = useState(1);
	const [assignProducts, { loading: assigning }] = useAssignProductsToOrganizationMutation();

	const { data, loading } = useGetAllOrganizationQuery({
		variables: {
			input: {
				pagination: { limit: 20, currentPage: page },
				search: debouncedQuery || undefined,
			},
		},
		fetchPolicy: 'no-cache',
		skip: !open,
	});

	const scroll = useInfiniteScroll({
		items: data?.getAllOrganization?.data,
		pageInfo: data?.getAllOrganization?.pageInfo,
		loading,
		resetKey: debouncedQuery,
	});

	if (scroll.page !== page) setPage(scroll.page);

	const handleOpenChange = (next: boolean) => {
		if (!next) {
			setSelectedOrg('');
			setSelectedBrand('');
			setQuery('');
			scroll.reset();
			setPage(1);
		}
		onOpenChange(next);
	};

	const handleAssign = async () => {
		if (!selectedOrg) return;
		try {
			const { data } = await assignProducts({
				variables: {
					productIds,
					organizationId: selectedOrg,
					brandId: selectedBrand || undefined,
				},
			});
			if (!data?.assignProductsToOrganization?.success) {
				toast.error(
					data?.assignProductsToOrganization?.message || 'Failed to assign products'
				);
				return;
			}
			const orgName = scroll.items.find((o) => o.id === selectedOrg)?.name;
			toast.success(
				`${productIds.length} product${productIds.length > 1 ? 's' : ''} assigned to ${orgName}`
			);
			onSuccess();
			handleOpenChange(false);
		} catch (err) {
			/* Carries the server's reason, e.g. a brand that belongs to another client. */
			toast.error(err instanceof Error ? err.message : 'Failed to assign products');
		}
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Assign to client</DialogTitle>
					<DialogDescription>
						Select a client to assign {productIds.length} product
						{productIds.length > 1 ? 's' : ''} to.
					</DialogDescription>
				</DialogHeader>

				<DialogBody className="flex flex-col gap-0 p-0">
					<div className="relative shrink-0 px-4 pt-4 pb-2">
						<Search
							size={14}
							className="pointer-events-none absolute left-7 top-1/2 -translate-y-1/2 text-text-4"
						/>
						<input
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder="Search clients..."
							className="h-8 w-full rounded-[var(--r-2)] border border-line-2 bg-surface-input pl-9 pr-3 text-base text-text-1 outline-none placeholder:text-text-4 focus:border-line-3"
							autoFocus
						/>
					</div>

					<div className="scroll-thin h-[300px] overflow-y-auto px-4 py-1">
						<div className="flex flex-col gap-0.5">
							{scroll.items.map((org) => {
								const isSelected = selectedOrg === org.id;
								return (
									<button
										key={org.id}
										onClick={() => {
											if (org.id !== selectedOrg) setSelectedBrand('');
											setSelectedOrg(org.id);
										}}
										className={cn(
											'flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-surface-hover',
											isSelected && 'bg-surface-fill'
										)}
									>
										<Checkbox
											checked={isSelected}
											tabIndex={-1}
											className="pointer-events-none"
										/>
										<InitialsAvatar
											name={org.name}
											src={org.logo}
											size={30}
											shape="rounded"
										/>
										<div className="min-w-0 flex-1">
											<div className="truncate text-base font-medium text-text-1">
												{org.name}
											</div>
											{org.organizationIndustry?.name && (
												<div className="truncate text-xs text-text-4">
													{org.organizationIndustry.name}
												</div>
											)}
										</div>
									</button>
								);
							})}
							{scroll.initialLoading && (
								<div className="flex items-center justify-center py-6 text-sm text-text-4">
									Loading...
								</div>
							)}
							{!loading && scroll.items.length === 0 && !scroll.initialLoading && (
								<div className="flex items-center justify-center py-6 text-center text-sm text-text-4">
									{query
										? 'No clients match your search.'
										: 'No clients available.'}
								</div>
							)}
							{scroll.loadingMore && (
								<div className="py-2 text-center text-xs text-text-4">
									Loading more...
								</div>
							)}
							<div ref={scroll.sentinelRef} className="h-1" />
						</div>
					</div>

					{selectedOrg && (
						<div className="flex shrink-0 flex-col gap-1.5 border-t border-line-1 px-4 py-3">
							<Label>Brand</Label>
							<BrandSelector
								value={selectedBrand}
								onChange={setSelectedBrand}
								organizationId={selectedOrg}
								clearable
								placeholder="No brand"
							/>
						</div>
					)}
				</DialogBody>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={assigning}
					>
						Cancel
					</Button>
					<Button onClick={handleAssign} disabled={!selectedOrg || assigning}>
						{assigning ? 'Assigning...' : 'Assign'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
